/* Util functions that consume OR produce carePath objects can live here */
const { UMR_OPI_TO_DELSYS } = require('../constants/umrOpiToDelsysMapping');
const {
    UHC_PARTNER_ID,
    LINE_OF_BUSINESS,
    MEMBERSHIP_CATEGORY,
    COVERAGE_TYPE_CODE,
    OUTPATIENT,
    ORGANIZATION,
    PERSON,
    UMR_VISION_PROD_PLANIND,
    UMR_POPULATIONS,
    CARE_PATH_CATEGORY_CODE,
} = require('../constants/index');

/**
 * used as a backup in case a "Standard" (aka non "BillingCode") type carePath
 * does not contain a line item whose billing code equals the mainBillingCode
 * per CarePath API team, using a combination of primaryFacilityTypeCode and isPrimaryLineItem
 * should get us to a single line item that we could consider the primary
 */
export const determinePrimaryServiceForNonBillingCodeCarePath = (carePathDataResponse) => {
    let selectedService = {};
    const primaryFacilityTypeCategoryCode =
        carePathDataResponse.primaryFacilityTypeCategoryCode.toLowerCase();

    carePathDataResponse.carePathSteps.forEach((step) => {
        step.billingInformation.possibleClaims.forEach((possibleClaim) => {
            if (
                possibleClaim.primaryFacilityTypeCode?.toLowerCase() ==
                primaryFacilityTypeCategoryCode
            ) {
                possibleClaim.lineItems.forEach((lineItem) => {
                    if (lineItem.isPrimaryLineItem == true) {
                        selectedService = {
                            fullCarePath: carePathDataResponse,
                            lineItem: lineItem,
                            carePathStep: step,
                            name: carePathDataResponse.name,
                        };
                    }
                });
            }
        });
    });

    return selectedService;
};

/**
 * Formats the response to match what use when there are carePathStepsAvailable
 * @param {*} carePathDataResponse
 * @returns
 */
export const formatCarePathResponse = (carePathDataResponse) => {
    const lineItem = {
        benefitCode: {
            code: '',
        },
        billingCode: {
            system: carePathDataResponse.mainBillingCodeSystem,
            code: carePathDataResponse.mainBillingCode,
        },
        content: carePathDataResponse.content,
        isPreventativeCost: carePathDataResponse.preventive,
        placeOfServiceCode: {
            code: '',
        },
        providerTypeSequence: null,
        quantity: 1, // todo how would we know?
    };
    return {
        fullCarePath: carePathDataResponse,
        lineItem: lineItem,
        carePathStep: carePathDataResponse.carePathSteps,
        name: carePathDataResponse.name,
    };
};

const assignSelectedServiceObject = (fullCarePath, lineItem, carePathStep) => {
    return {
        fullCarePath: fullCarePath,
        lineItem: lineItem,
        carePathStep: carePathStep,
        name: fullCarePath.name,
    };
};

export const getSelectedServiceForFacilityCharge = (serviceObj) => {
    const organizationTypeCarePathSteps = serviceObj.carePathSteps.filter(
        (step) => step.providerType == ORGANIZATION
    );

    for (const carePathStep of organizationTypeCarePathSteps) {
        for (const possibleClaim of carePathStep.billingInformation.possibleClaims) {
            if (
                possibleClaim.primaryFacilityTypeCode == serviceObj.primaryFacilityTypeCategoryCode
            ) {
                const matchedLineItem = possibleClaim.lineItems.find(
                    (lineItem) => lineItem.isPrimaryLineItem && lineItem.revenueCode
                );
                if (matchedLineItem) {
                    return assignSelectedServiceObject(serviceObj, matchedLineItem, carePathStep);
                }
            }
        }
    }
    return null;
};

/**
 * function used to yield the single line item that we could consider the primary selected service within a carePath
 * within the care path step, match on primaryFacilityTypeCode to find the claim and then within claim,
 * find the line item whose billing code equals the mainBillingCode
 * if more than 1 matching service, for primary service find the one that has ProviderType = Person (phsyician charge)
 * and has isPrimaryLineItem = true
 */
export const getPrimarySelectedService = (serviceObj) => {
    let selectedService;
    let matchingServices = [];

    serviceObj?.carePathSteps.forEach((carePathStep) => {
        carePathStep?.billingInformation?.possibleClaims.forEach((possibleClaim) => {
            if (
                possibleClaim.primaryFacilityTypeCode == serviceObj.primaryFacilityTypeCategoryCode
            ) {
                possibleClaim?.lineItems.forEach((lineItem) => {
                    if (
                        lineItem?.billingCode?.code?.toLowerCase() ==
                        serviceObj?.mainBillingCode?.toLowerCase()
                    ) {
                        if (matchingServices.length > 0) {
                            if (lineItem.isPrimaryLineItem) {
                                matchingServices.push(
                                    assignSelectedServiceObject(serviceObj, lineItem, carePathStep)
                                );
                            }
                        } else {
                            matchingServices.push(
                                assignSelectedServiceObject(serviceObj, lineItem, carePathStep)
                            );
                        }
                    }
                });
            }
        });
    });

    selectedService = prioritizeMatchingServices(matchingServices);

    if (!selectedService) {
        selectedService = determinePrimaryServiceForNonBillingCodeCarePath(serviceObj);
    }

    // if outpatient service, and primary service is for physician, call func to get service info for facility charge
    if (
        serviceObj?.category?.label == OUTPATIENT &&
        selectedService.lineItem?.providerType == PERSON
    ) {
        selectedService.facilityChargeInfo = getSelectedServiceForFacilityCharge(serviceObj);
    }

    return selectedService;
};

export const prioritizeMatchingServices = (matchingServices) => {
    matchingServices = matchingServices.sort((a, b) => {
        return sortByProviderType(a, b) || sortByPrimaryLineItem(a, b);
    });
    return matchingServices[0];
};

const sortByPrimaryLineItem = (a, b) => {
    if (a?.lineItem?.isPrimaryLineItem) {
        return -1;
    } else if (b?.lineItem?.isPrimaryLineItem) {
        return 1;
    }
    return 0;
};

const sortByProviderType = (a, b) => {
    if (a?.lineItem?.providerType == PERSON) {
        return -1;
    } else if (b?.lineItem?.providerType == PERSON) {
        return 1;
    }
    return 0;
};

/**
 * Derives the plan code value to be sent as optional field in care path api requests
 * Uses an opi to delsys code mapping to get the value for UMR members
 * Uses the reciprocity id from plan summary to get the value for members where it exists
 * TODO: what about members where we don't have a reciprocity id - is planCode even relevant for them?
 */
export const derivePlanCode = (memberDetails, carePathCategoryCode) => {
    try {
        const coverageTypeCode =
            memberDetails?.sourceCode == 'PH' ? COVERAGE_TYPE_CODE.OPH : carePathCategoryCode;

        const relevantCoverage =
            memberDetails?.eligibility[0]?.memberHealthCoverage?.coverageType.find(
                (coverage) => coverage?.typeCode?.coverageTypeCode === coverageTypeCode
            );

        if (memberDetails?.sourceCode == 'UM') {
            if (relevantCoverage?.opi) {
                return UMR_OPI_TO_DELSYS[relevantCoverage?.opi];
            } else {
                //use fallback logic to return first non-empty opi if the opi in relevant coverageType is empty
                const nonCoverageWithEmptyOpi =
                    memberDetails?.eligibility[0]?.memberHealthCoverage?.coverageType.find(
                        (coverage) => {
                            if (coverage?.opi) return true;
                            else return false;
                        }
                    );
                return UMR_OPI_TO_DELSYS[nonCoverageWithEmptyOpi.opi];
            }
        }
        const networkWithReciprocityId = relevantCoverage.networks.find((network) => {
            return network.type == 'RECIPROCITYID';
        });
        return networkWithReciprocityId?.planNetworkInd;
    } catch (error) {
        return null;
    }
};

export const getPartnerId = (memberDetailsResponse) => {
    const memberLOB = memberDetailsResponse?.lineOfBusiness;
    const memberMembershipCategory = memberDetailsResponse?.membershipCategory;
    const memberPayer = memberDetailsResponse?.popConfig?.payer;

    if (
        memberLOB == LINE_OF_BUSINESS.ENI &&
        memberMembershipCategory == MEMBERSHIP_CATEGORY.OXFORD
    ) {
        return UHC_PARTNER_ID.OXFORD;
    } else if (
        memberLOB == LINE_OF_BUSINESS.ENI &&
        memberMembershipCategory == MEMBERSHIP_CATEGORY.UHC_WEST
    ) {
        return UHC_PARTNER_ID.OXFORD;
    } else if (memberLOB == LINE_OF_BUSINESS.ENI) {
        return UHC_PARTNER_ID.DEFAULT_UHC;
    } else if (memberLOB == LINE_OF_BUSINESS.MNR) {
        return UHC_PARTNER_ID.MEDICARE;
    } else if (
        memberLOB == LINE_OF_BUSINESS.CNS &&
        MEMBERSHIP_CATEGORY.PUBLIC_EXCHANGE.includes(memberMembershipCategory)
    ) {
        return UHC_PARTNER_ID.EXCHANGE;
    } else if (memberLOB == LINE_OF_BUSINESS.CNS) {
        return UHC_PARTNER_ID.MEDICAID;
    } else if (memberPayer == 'UHC') {
        return UHC_PARTNER_ID.DEFAULT_UHC;
    } else if (memberPayer) {
        return UHC_PARTNER_ID.ADVANTAGE;
    } else {
        return UHC_PARTNER_ID.DEFAULT_UHC;
    }
};

/**
 * function to check if the member has UMR vision coverage and vision coverageType has prodNetworkInd value is equal to 'V_IDM'
 * @param {*} memberDetails object containing member eligibility details
 * @returns boolean, true if member has UMR vision coverage and vision coverageType has prodNetworkInd value is equal to 'V_IDM'
 */
export const hasUMRVisionCoverageWithSpecificNetworkInd = (memberDetails) => {
    const isUMRMember = UMR_POPULATIONS?.includes(memberDetails?.sourceCode?.toUpperCase());
    //check if the member has vision coverage
    const hasVisionCoverage = memberDetails?.eligibility[0]?.memberHealthCoverage.coverageType.find(
        (coverageType) => coverageType.typeCode.coverageTypeCode === 'V'
    );
    //check if hasVisoCoverage is defined and if prodNetworkInd value inside hasVisionCoverage object is eaual to 'V_IDM'
    return (
        isUMRMember &&
        hasVisionCoverage &&
        hasVisionCoverage?.prodNetworkInd === UMR_VISION_PROD_PLANIND
    );
};

/**
 * function to get the coverageTypeCodes from memberDetails object
 * @param {*} memberDetails object containing member eligibility details
 * @returns array of coverageTypeCodes from memberDetails object
 * OPH coverageTypeCode will be converted to A coverageTypeCode as API expects A for OPH
 */

export const getMemberCoverageTypeCodes = (memberDetails) => {
    const validCodes = new Set(Object.values(CARE_PATH_CATEGORY_CODE));
    return (
        memberDetails?.eligibility?.[0]?.memberHealthCoverage?.coverageType
            ?.map((coverageType) =>
                coverageType?.typeCode?.coverageTypeCode === COVERAGE_TYPE_CODE.OPH
                    ? CARE_PATH_CATEGORY_CODE.PHYSICAL
                    : coverageType?.typeCode?.coverageTypeCode
            )
            .filter((code) => validCodes.has(code)) || []
    );
};
